import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import type { Playlist } from '@ghostscreen/contracts';
import { ConfirmDialog } from '../../components/dashboard/ConfirmDialog';
import { apiErrorMessage } from '../../i18n';
import { api } from '../../lib/api';
import type { GroupCardModel } from './buildGroupCardModel';
import { playlistOptions } from './buildGroupCardModel';
import { BatchProgress } from './BatchProgress';

type GroupCardProps = {
  organisationId: string;
  model: GroupCardModel;
  playlists: Playlist[];
  onEdit: () => void;
  onAssigned: () => void;
};

/** Group card — Ghost Screen Groups.dc.html. */
export function GroupCard({
  organisationId,
  model,
  playlists,
  onEdit,
  onAssigned,
}: GroupCardProps) {
  const { t } = useTranslation(['groups', 'common']);
  const [feedback, setFeedback] = useState<string | null>(null);
  const [confirmRestart, setConfirmRestart] = useState(false);
  const [batchId, setBatchId] = useState<string | null>(null);

  const assignAll = useMutation({
    mutationFn: async (playlistId: string) => {
      const paired = model.memberScreens.filter((screen) => screen.deviceId != null);
      await Promise.all(
        paired.map((screen) =>
          api.assignPlaylistToScreen(organisationId, screen.id, playlistId),
        ),
      );
      return paired.length;
    },
    onSuccess: (count) => {
      setFeedback(null);
      onAssigned();
      if (!count) setFeedback(t('card.noSetUpScreens'));
    },
    onError: (err) => setFeedback(apiErrorMessage(err, t('card.assignError'))),
  });

  const restart = useMutation({
    mutationFn: () =>
      api.createDeviceCommandBatch(
        organisationId,
        { screenGroupId: model.group.id, type: 'RESTART_PLAYER' },
        crypto.randomUUID(),
      ),
    onSuccess: (batch) => {
      setConfirmRestart(false);
      setBatchId(batch.id);
      setFeedback(null);
    },
    onError: (err) => {
      setConfirmRestart(false);
      setFeedback(apiErrorMessage(err, t('card.restartError')));
    },
  });

  const options = playlistOptions(playlists, model.playlistPlaceholder);
  const liveCount = model.memberScreens.filter((screen) => screen.deviceId != null).length;

  return (
    <section
      className="flex flex-col gap-4 rounded-[14px] border px-[22px] py-5"
      style={{ background: 'var(--gs-panel)', borderColor: 'var(--gs-line)' }}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 flex-col gap-[5px]">
          <span
            className="text-[17px] font-extrabold tracking-[-0.02em]"
            style={{ color: 'var(--gs-ink)' }}
          >
            {model.group.name}
          </span>
          <span
            className="flex flex-wrap items-center gap-1.5 text-[13px]"
            style={{ color: 'var(--gs-muted)' }}
          >
            <span>{model.meta}</span>
            <span>·</span>
            <span className="font-semibold" style={{ color: model.healthColor }}>
              {model.health}
            </span>
          </span>
        </div>
        <button
          type="button"
          onClick={onEdit}
          className="rounded-[9px] border px-3 py-[7px] text-[13px] font-bold"
          style={{
            color: 'var(--gs-ink)',
            background: 'var(--gs-panel)',
            borderColor: 'var(--gs-line)',
            fontFamily: 'inherit',
          }}
        >
          {t('common:actions.edit')}
        </button>
      </div>

      <div className="flex flex-wrap gap-1.5">
        {model.members.map((member) => (
          <span
            key={member.id}
            className="inline-flex items-center gap-[7px] rounded-full border px-[11px] py-1.5 text-[13px] font-semibold"
            style={{
              color: 'var(--gs-ink2)',
              borderColor: 'var(--gs-line)',
            }}
          >
            <span
              className="h-[7px] w-[7px] rounded-full"
              style={{ background: member.dot }}
              aria-hidden
            />
            {member.name}
          </span>
        ))}
        {model.noMembers ? (
          <button
            type="button"
            onClick={onEdit}
            className="rounded-full border-[1.5px] border-dashed px-3 py-1.5 text-[13.5px] font-semibold"
            style={{
              color: 'var(--gs-accent)',
              background: 'none',
              borderColor: 'var(--gs-line)',
              fontFamily: 'inherit',
            }}
          >
            {t('card.addScreens')}
          </button>
        ) : null}
      </div>

      <label className="flex flex-col gap-[7px]">
        <span className="text-[12.5px] font-bold" style={{ color: 'var(--gs-ink2)' }}>
          {t('card.showOnEvery')}
        </span>
        <select
          value={model.playlistValue}
          disabled={assignAll.isPending || model.noMembers || playlists.length === 0}
          onChange={(event) => {
            const value = event.target.value;
            if (!value) return;
            assignAll.mutate(value);
          }}
          className="w-full cursor-pointer rounded-[10px] border px-3 py-[11px] text-sm font-semibold outline-none focus:border-[var(--gs-accent)] disabled:opacity-50"
          style={{
            color: 'var(--gs-ink)',
            background: 'var(--gs-field)',
            borderColor: 'var(--gs-line)',
            fontFamily: 'inherit',
          }}
        >
          {options.map((option) => (
            <option key={option.id || 'placeholder'} value={option.id}>
              {option.name}
            </option>
          ))}
        </select>
      </label>

      <div
        className="flex flex-wrap gap-2 border-t pt-3.5"
        style={{ borderColor: 'var(--gs-line)' }}
      >
        <Link
          to={`/schedule?new=1&groupId=${encodeURIComponent(model.group.id)}`}
          className="rounded-[9px] border px-3.5 py-[9px] text-[13.5px] font-bold no-underline"
          style={{
            color: 'var(--gs-ink)',
            background: 'var(--gs-panel)',
            borderColor: 'var(--gs-line)',
          }}
        >
          {t('card.setTimes')}
        </Link>
        <button
          type="button"
          disabled={liveCount === 0 || restart.isPending}
          onClick={() => setConfirmRestart(true)}
          className="rounded-[9px] border px-3.5 py-[9px] text-[13.5px] font-bold disabled:opacity-50"
          style={{
            color: 'var(--gs-ink)',
            background: 'var(--gs-panel)',
            borderColor: 'var(--gs-line)',
            fontFamily: 'inherit',
          }}
        >
          {t('card.restartAll')}
        </button>
      </div>

      {feedback ? (
        <p className="m-0 text-[13px]" style={{ color: 'var(--gs-muted)' }}>
          {feedback}
        </p>
      ) : null}

      {batchId ? (
        <BatchProgress organisationId={organisationId} batchId={batchId} />
      ) : null}

      {confirmRestart ? (
        <ConfirmDialog
          title={t('card.confirmRestartTitle', { name: model.group.name })}
          description={t('card.confirmRestartDescription', { count: liveCount })}
          confirmLabel={t('card.restartAll')}
          tone="danger"
          confirming={restart.isPending}
          onCancel={() => {
            if (!restart.isPending) setConfirmRestart(false);
          }}
          onConfirm={() => restart.mutate()}
        />
      ) : null}
    </section>
  );
}
